import { Box, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import { ErrorOutline, WarningAmber, LightbulbOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { dashboardService, type DashboardTip } from '../../../services/dashboard.service';
import { useWorkspaceStore } from '../../../stores/workspace.store';

// Status colours — reserved, always paired with an icon + word, never colour alone.
const SERIES = '#2a78d6';
const BAD = '#d03b3b';
const WARN = '#b06f00';
const GOOD = '#0ca30c';

const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 } as const;

/** Most actionable first: refuted sink, then severity, agent-confirmed ahead, then impact. */
function sortTips<T extends DashboardTip>(tips: T[]): T[] {
  return [...tips].sort((a, b) => {
    const refuted = (t: DashboardTip) => (t.review?.verdict === 'refuted' ? 1 : 0);
    if (refuted(a) !== refuted(b)) return refuted(a) - refuted(b);
    const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (sev !== 0) return sev;
    const confirmed = (t: DashboardTip) => (t.review?.verdict === 'confirmed' ? 0 : 1);
    if (confirmed(a) !== confirmed(b)) return confirmed(a) - confirmed(b);
    return (b.impact || 0) - (a.impact || 0);
  });
}

/** `workspace` only when several are in view. */
function TipRow({ tip, first, workspace, date }: { tip: DashboardTip; first: boolean; workspace?: string; date?: string }) {
  const meta = {
    critical: { color: BAD, Icon: ErrorOutline, label: 'Critical' },
    warning: { color: WARN, Icon: WarningAmber, label: 'Watch' },
    info: { color: SERIES, Icon: LightbulbOutlined, label: 'Idea' },
  }[tip.severity] || { color: SERIES, Icon: LightbulbOutlined, label: 'Idea' };
  const { color, Icon, label } = meta;

  const verdict = tip.review?.verdict;
  const refuted = verdict === 'refuted';
  const badge = tip.source === 'agent'
    ? { text: 'AI', color: SERIES }
    : verdict === 'confirmed' ? { text: 'Scanner · AI confirmed', color: GOOD }
      : verdict === 'revised' ? { text: 'Scanner · AI revised', color: WARN }
        : refuted ? { text: 'Scanner · AI refuted', color: 'text.secondary' }
          : { text: 'Scanner', color: 'text.secondary' };

  return (
    <Box sx={{ opacity: refuted ? 0.6 : 1 }}>
      {!first && <Divider />}
      <Box sx={{ display: 'flex', gap: 1.5, py: 1.25, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color, minWidth: 78, flexShrink: 0, pt: 0.25 }}>
          <Icon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
        </Box>
        {workspace && (
          <Typography variant="caption" noWrap title={date ? `${workspace} · ${date}` : workspace}
            sx={{ width: 110, flexShrink: 0, pt: 0.25, fontWeight: 600, color: 'text.primary' }}>
            {workspace}
          </Typography>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: refuted ? 'line-through' : 'none' }}>
              {tip.title}
            </Typography>
            <Chip
              label={badge.text}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', color: badge.color, borderColor: badge.color }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{tip.detail}</Typography>
          {tip.review?.note && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.25 }}>
              AI: {tip.review.note}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** The daily briefings of every workspace in view (selection or pin), as one list. */
export default function SummaryPanel({ workspaceId }: { workspaceId?: string }) {
  const selectedIds = useWorkspaceStore((s) => s.selectedIds);
  const { data: briefings = [], isLoading } = useQuery({
    queryKey: ['dashboard-briefings', workspaceId ?? (selectedIds.join(',') || 'all')],
    queryFn: () => dashboardService.getBriefings(workspaceId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} /></Box>;
  }
  if (briefings.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No briefing yet.</Typography>
      </Box>
    );
  }

  // Sorted across workspaces, not within each: the most urgent tip leads wherever it is.
  const several = briefings.length > 1;
  const tips = sortTips(briefings.flatMap((b) => b.tips.map((t) => ({ ...t, workspace: b.workspace_name, date: b.date }))));
  const dates = [...new Set(briefings.map((b) => b.date))];
  if (tips.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">Nothing needs attention — nice.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ height: '100%', overflowY: 'auto', pr: 0.5 }}>
      {dates.length === 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {format(new Date(`${dates[0]}T00:00:00Z`), 'EEE, MMM d')}
        </Typography>
      )}
      {tips.map((t, i) => (
        <TipRow key={i} tip={t} first={i === 0} workspace={several ? t.workspace : undefined} date={t.date} />
      ))}
    </Box>
  );
}
